import { Delete, RotateCcw } from 'lucide-react';
import React, { useCallback, useEffect, useRef } from 'react';

interface PasscodeInputProps {
  code: string;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  isLoading?: boolean;
  /** Number of digits in the passcode. Defaults to 4. */
  length?: number;
}

export const PasscodeInput: React.FC<PasscodeInputProps> = ({
  code,
  onChange,
  onSubmit,
  isLoading = false,
  length = 4,
}) => {
  const slotKeys = Array.from({ length }, (_, i) => `slot-${i}`);

  // Handle keypad number press
  const handlePressNumber = useCallback(
    (num: string) => {
      if (isLoading || code.length >= length) return;
      const nextCode = code + num;
      onChange(nextCode);
    },
    [code, isLoading, onChange, length],
  );

  // Handle backspace
  const handleBackspace = useCallback(() => {
    if (isLoading || code.length === 0) return;
    onChange(code.slice(0, -1));
  }, [code, isLoading, onChange]);

  // Handle clear
  const handleClear = useCallback(() => {
    if (isLoading) return;
    onChange('');
  }, [isLoading, onChange]);

  // Trigger submit once the code reaches the required length. Guards against
  // submitting the same completed code more than once — isLoading can briefly
  // flip back to false mid-flow (the caller may dispatch more than one loading
  // action in sequence on success), which would otherwise let this effect
  // re-fire and submit again while the first submission is still finishing.
  const lastSubmittedCodeRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      code.length === length &&
      !isLoading &&
      lastSubmittedCodeRef.current !== code
    ) {
      lastSubmittedCodeRef.current = code;
      onSubmit(code);
    }
  }, [code, onSubmit, isLoading, length]);

  // Physical keyboard listeners
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isLoading) return;

      if (/^\d$/.test(e.key)) {
        handlePressNumber(e.key);
      } else if (e.key === 'Backspace') {
        handleBackspace();
      } else if (e.key === 'Escape') {
        handleClear();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handlePressNumber, handleBackspace, handleClear, isLoading]);

  return (
    <div className="flex flex-col items-center select-none w-full max-w-sm mx-auto">
      {/* Digit Display Slots or Loading Spinner */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center h-12 mb-5 w-full bg-green-50 border border-green-100 rounded-xl animate-pulse">
          <div className="flex items-center gap-2 text-pos-primary">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-pos-primary border-t-transparent" />
            <span className="text-sm font-semibold tracking-wide">
              Verifying passcode...
            </span>
          </div>
        </div>
      ) : (
        <div className="flex justify-center gap-2.5 mb-5 w-full">
          {slotKeys.map((slotKey, i) => (
            <div
              key={slotKey}
              className={`w-9 h-12 rounded-lg border-2 flex items-center justify-center text-xl font-bold transition-all duration-150 ${
                i < code.length
                  ? 'border-pos-primary bg-green-50 text-pos-primary ring-1 ring-pos-primary'
                  : 'border-gray-200 bg-gray-50 text-gray-400'
              }`}
            >
              {i < code.length ? '•' : ''}
            </div>
          ))}
        </div>
      )}

      {/* Numeric Keypad Grid */}
      <div className="grid grid-cols-3 gap-3.5 w-full">
        {/* Rows 1-3 */}
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((num) => (
          <button
            key={num}
            type="button"
            disabled={isLoading}
            onClick={() => handlePressNumber(num)}
            className="h-14 rounded-xl border border-gray-200 bg-white text-xl font-bold text-gray-900 hover:bg-gray-50 active:bg-gray-100 hover:border-gray-300 transition-all active:scale-[0.98] shadow-sm flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {num}
          </button>
        ))}

        {/* Row 4: Clear, 0, Backspace */}
        <button
          type="button"
          disabled={isLoading || code.length === 0}
          onClick={handleClear}
          className="h-14 rounded-xl border border-gray-200 bg-gray-50 text-xs font-semibold text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-all active:scale-[0.98] flex flex-col items-center justify-center gap-0.5 shadow-sm disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <RotateCcw size={15} />
          <span>Clear</span>
        </button>

        <button
          type="button"
          disabled={isLoading}
          onClick={() => handlePressNumber('0')}
          className="h-14 rounded-xl border border-gray-200 bg-white text-xl font-bold text-gray-900 hover:bg-gray-50 active:bg-gray-100 hover:border-gray-300 transition-all active:scale-[0.98] shadow-sm flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
        >
          0
        </button>

        <button
          type="button"
          disabled={isLoading || code.length === 0}
          onClick={handleBackspace}
          className="h-14 rounded-xl border border-gray-200 bg-gray-50 hover:bg-gray-100 active:bg-gray-200 text-gray-700 transition-all active:scale-[0.98] flex flex-col items-center justify-center gap-0.5 shadow-sm disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <Delete size={16} />
          <span className="text-[10px] font-semibold">Delete</span>
        </button>
      </div>
    </div>
  );
};
